import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import {
  AquasoftCompanySearchRow,
  CompanyLinkRow,
  IncomingPurchaseOrderRow,
  UnlinkedAccountRow,
  createManualCompanyLink,
  getCompanyLinks,
  getIncomingPurchaseOrders,
  reconnectCompanyLink,
  revokeCompanyLink,
  scanCompanyLinks,
  searchAquasoftCompanies,
  searchUnlinkedAccounts,
} from '../../../services/ecommerceApi';
import { getBranches } from '../../../services/migration/branchDataService';
import { useAuth } from '../../../contexts/AuthContext';
import IncomingOrderDetailDialog, {
  STATUS_LABELS,
  formatDate,
  formatMoney,
  statusChip,
} from './IncomingOrderDetailDialog';
import AccountingDataTable from '../shared/AccountingDataTable';
import GuideButton from '../../common/guide/GuideButton';
import type { DataTableColumn } from '../shared/AccountingDataTable';

// ============================================================
// B2B siparişi
//  - Üstte eşleştirme kodu (tedarikçi vitrinine girilir; şube adına üretilebilir)
//  - Merkez modu (embedded): yalnız Kod Al + Firma Bağlantıları
//    (gelen alış siparişleri artık Cari Siparişler listesinde gösterilir)
//  - Manager modu: Kod Al + kendi şubesinin gelen siparişleri + bağlantıları
//
// Şube kuralı: kod hangi şube adına üretildiyse bağlantı ve o bağlantıdan gelen
// tüm siparişler o şubeye yazılır; şubesiz kod = merkez.
// ============================================================

interface InterCompanyB2BProps {
  /** Siparişler sayfasına gömülü kullanımda başlık/dış boşluk kapanır. */
  embedded?: boolean;
  /** Şube yöneticisi paneli: her şey managerBranchId şubesine kilitlenir. */
  isManager?: boolean;
  managerBranchId?: string | null;
}

const InterCompanyB2B: React.FC<InterCompanyB2BProps> = ({
  embedded = false,
  isManager = false,
  managerBranchId = null,
}) => {
  // Gelen alış siparişleri listesi hem merkezde hem şube panelinde gösterilir.
  // Sahiplik değişmez: sipariş kaydı hep siparişi veren şubeye (IncomingPurchaseOrder.BranchId)
  // yazılır. Şube yalnız kendi siparişini görür; merkez tüm şubeleri şube filtresiyle görür.
  const showOrdersList = true;

  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId ? String(userDetails.companyId) : null;

  // ---------------- Şubeler ----------------
  const [branchMap, setBranchMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const branches = await getBranches(companyId);
        if (cancelled) return;
        const map: Record<string, string> = {};
        branches.forEach((b) => {
          map[b.id] = b.name || 'İsimsiz Şube';
        });
        setBranchMap(map);
      } catch {
        // Şube listesi yüklenemezse filtre boş kalır; sayfa çalışmaya devam eder.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const branchLabel = useCallback(
    (branchId?: string | null, branchName?: string | null) =>
      branchName || (branchId ? branchMap[branchId] || branchId : 'Merkez'),
    [branchMap]
  );

  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [linkStatusTab, setLinkStatusTab] = useState<'active' | 'revoked'>('active');

  // ---------------- Gelen siparişler (yalnız manager) ----------------
  const [orders, setOrders] = useState<IncomingPurchaseOrderRow[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [invoicedFilter, setInvoicedFilter] = useState(''); // '' | 'true' | 'false'
  // Merkez sipariş listesi şube filtresi: 'all' | 'hq' | branchId. Manager'da kullanılmaz.
  const [orderBranchFilter, setOrderBranchFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const loadOrders = useCallback(async () => {
    if (!showOrdersList) return;
    setOrdersLoading(true);
    try {
      const res = await getIncomingPurchaseOrders({
        status: statusFilter || undefined,
        invoiced: invoicedFilter === '' ? undefined : invoicedFilter === 'true',
        // Manager kendi şubesine kilitli; merkez şube filtresini uygular
        // ('all' → tüm şubeler, 'hq' → yalnız merkez, aksi halde seçili şube).
        branchId: isManager
          ? (managerBranchId || undefined)
          : (orderBranchFilter === 'all' ? undefined : orderBranchFilter),
        page: page + 1,
        pageSize,
      });
      setOrders(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Gelen siparişler yüklenemedi.', severity: 'error' });
    } finally {
      setOrdersLoading(false);
    }
  }, [showOrdersList, statusFilter, invoicedFilter, isManager, managerBranchId, orderBranchFilter, page, pageSize]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // ---------------- Detay dialogu ----------------
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const openDetail = (orderId: string) => {
    setDetailOrderId(orderId);
    setDetailOpen(true);
  };

  // ---------------- Firma bağlantıları ----------------
  const [links, setLinks] = useState<CompanyLinkRow[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const loadLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      // Eşleştirme kodu kaldırıldı: panel açılışında carileri tara, Aquasoft firmalarını otomatik bağla.
      try {
        await scanCompanyLinks();
      } catch {
        // Tarama başarısız olsa da mevcut bağlantılar yine listelenir.
      }
      const res = await getCompanyLinks();
      const rows = res.data || [];
      // Manager yalnızca kendi şubesinin alıcı bağlantılarını görür.
      // Merkez: şube filtresi (all / hq / branchId) satıcı+alıcı satırlarına uygulanır.
      const scoped = isManager
        ? rows.filter((l) => l.role === 'buyer' && (l.buyerBranchId || null) === (managerBranchId || null))
        : rows;
      setLinks(scoped);
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Bağlantılar yüklenemedi.', severity: 'error' });
    } finally {
      setLinksLoading(false);
    }
  }, [isManager, managerBranchId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // ---------------- Elle eşleştirme (güvenlik ağı) ----------------
  // Otomatik yolların (Aquasoft girişi / vergi no / e-posta) hiçbiri çalışmadıysa
  // satıcı, bağlantısız bir cariyi Aquasoft firmasıyla elle bağlayabilir.
  const [manualAccount, setManualAccount] = useState<UnlinkedAccountRow | null>(null);
  const [manualCompany, setManualCompany] = useState<AquasoftCompanySearchRow | null>(null);
  const [accountOptions, setAccountOptions] = useState<UnlinkedAccountRow[]>([]);
  const [companyOptions, setCompanyOptions] = useState<AquasoftCompanySearchRow[]>([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [manualLinking, setManualLinking] = useState(false);

  const loadAccountOptions = useCallback(async (q?: string) => {
    setAccountSearchLoading(true);
    try {
      const res = await searchUnlinkedAccounts(q);
      setAccountOptions(res.data || []);
    } catch {
      // Arama hatası sessiz geçilir; kullanıcı yeniden yazınca tekrar denenir.
    } finally {
      setAccountSearchLoading(false);
    }
  }, []);

  const loadCompanyOptions = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setCompanyOptions([]);
      return;
    }
    setCompanySearchLoading(true);
    try {
      const res = await searchAquasoftCompanies(q.trim());
      setCompanyOptions(res.data || []);
    } catch {
      // Arama hatası sessiz geçilir.
    } finally {
      setCompanySearchLoading(false);
    }
  }, []);

  // Elle eşleştirme yalnız satıcı (merkez) panelinde gösterilir; cari listesi açılışta gelir.
  useEffect(() => {
    if (!isManager) loadAccountOptions();
  }, [isManager, loadAccountOptions]);

  const handleManualLink = async () => {
    if (!manualAccount || !manualCompany) return;
    setManualLinking(true);
    try {
      const res = await createManualCompanyLink(manualAccount.id, manualCompany.companyId);
      setSnack({ message: res.message || 'Eşleştirme kuruldu.', severity: 'success' });
      setManualAccount(null);
      setManualCompany(null);
      setCompanyOptions([]);
      await Promise.all([loadLinks(), loadAccountOptions()]);
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Eşleştirme kurulamadı.', severity: 'error' });
    } finally {
      setManualLinking(false);
    }
  };

  const [revokeTarget, setRevokeTarget] = useState<CompanyLinkRow | null>(null);
  const [revoking, setRevoking] = useState(false);

  const closeRevokeDialog = () => {
    if (revoking) return;
    setRevokeTarget(null);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeCompanyLink(revokeTarget.id);
      setSnack({ message: 'Bağlantı kesildi.', severity: 'success' });
      setRevokeTarget(null);
      await loadLinks();
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Bağlantı kesilemedi.', severity: 'error' });
    } finally {
      setRevoking(false);
    }
  };

  const [reconnectTarget, setReconnectTarget] = useState<CompanyLinkRow | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const closeReconnectDialog = () => {
    if (reconnecting) return;
    setReconnectTarget(null);
  };

  const confirmReconnect = async () => {
    if (!reconnectTarget) return;
    setReconnecting(true);
    try {
      const res = await reconnectCompanyLink(reconnectTarget.id);
      setSnack({ message: res.message || 'Bağlantı yeniden kuruldu.', severity: 'success' });
      setReconnectTarget(null);
      await loadLinks();
    } catch (err: any) {
      setSnack({ message: err?.response?.data?.message || 'Bağlantı yeniden kurulamadı.', severity: 'error' });
    } finally {
      setReconnecting(false);
    }
  };

  // Önce şube, sonra durum sekmesi uygulanır.
  const branchFiltered = isManager || branchFilter === 'all'
    ? links
    : links.filter((l) =>
        branchFilter === 'hq'
          ? !l.buyerBranchId
          : (l.buyerBranchId || null) === branchFilter
      );

  const activeCount = branchFiltered.filter((l) => l.status === 'Active').length;
  const revokedCount = branchFiltered.filter((l) => l.status === 'Revoked').length;

  const visibleLinks = branchFiltered.filter((l) =>
    linkStatusTab === 'active' ? l.status === 'Active' : l.status === 'Revoked'
  );

  const linkBranchOptions = Array.from(
    new Map(
      links
        .filter((l) => l.buyerBranchId)
        .map((l) => [l.buyerBranchId as string, l.buyerBranchName || branchMap[l.buyerBranchId as string] || l.buyerBranchId as string])
    ).entries()
  );

  const linkColumns = useMemo<DataTableColumn<CompanyLinkRow>[]>(() => {
    const columns: DataTableColumn<CompanyLinkRow>[] = [
    {
      id: 'rowNumber',
      label: 'Sıra',
      width: '56px',
      align: 'center',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {index + 1}
        </Box>
      ),
    },
    {
      id: 'role',
      label: 'Rolünüz',
      render: (row) => (
        <Chip
          size="small"
          color={row.role === 'buyer' ? 'primary' : 'secondary'}
          variant="outlined"
          label={row.role === 'buyer' ? 'Alıcı (siz sipariş veriyorsunuz)' : 'Satıcı (size sipariş geliyor)'}
        />
      ),
    },
    {
      id: 'counterpartCompanyName',
      label: 'Karşı Firma',
      field: 'counterpartCompanyName',
    },
    {
      id: 'branch',
      label: 'Şube',
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.buyerBranchId ? 'primary' : 'default'}
          label={branchLabel(row.buyerBranchId, row.buyerBranchName)}
        />
      ),
    },
    {
      id: 'sellerAccountName',
      label: 'Cari',
      render: (row) => row.sellerAccountName || (row.role === 'seller' ? '—' : '-'),
    },
    {
      id: 'status',
      label: 'Durum',
      render: (row) =>
        row.status === 'Active'
          ? <Chip size="small" color="success" label="Aktif" />
          : <Chip size="small" color="default" label={`Kesildi (${formatDate(row.revokedAt)})`} />,
    },
    {
      id: 'createdAt',
      label: 'Bağlantı Tarihi',
      render: (row) => formatDate(row.createdAt),
    },
  ];

    // Bağlantı kesme ve yeniden bağlama yalnız merkez (admin) panelinde.
    // Şube yöneticisi bu düğmeleri görmez; API de aynı işlemi reddeder.
    if (!isManager) {
      columns.push({
        id: 'actions',
        label: 'İşlem',
        align: 'right',
        render: (row) => (
          <>
            {row.status === 'Active' && (
              <Tooltip title="Bağlantıyı kes">
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setRevokeTarget(row); }}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {row.status === 'Revoked' && (
              <Tooltip title="Yeniden bağla">
                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setReconnectTarget(row); }}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      });
    }

    return columns;
  }, [branchLabel, isManager]);

  const linksEmptyMessage = branchFiltered.length === 0
    ? (branchFilter !== 'all' && !isManager
        ? 'Bu şube filtresine uygun bağlantı yok.'
        : 'Henüz firma bağlantısı yok.')
    : (linkStatusTab === 'active'
        ? 'Devam eden bağlantı yok.'
        : 'Kesilmiş bağlantı yok.');

  const linksTable = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            Bağlı Firmalar
          </Typography>
          {!isManager && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Şube</InputLabel>
              <Select
                value={branchFilter}
                label="Şube"
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="hq">Merkez</MenuItem>
                {linkBranchOptions.map(([id, name]) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Yenile">
            <IconButton onClick={loadLinks} disabled={linksLoading}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <Tabs
          value={linkStatusTab}
          onChange={(_, v) => setLinkStatusTab(v)}
          sx={{ px: 2, minHeight: 44 }}
        >
          <Tab value="active" label={`Devam Eden (${activeCount})`} sx={{ minHeight: 44 }} />
          <Tab value="revoked" label={`Kesilen (${revokedCount})`} sx={{ minHeight: 44 }} />
        </Tabs>
        <Divider />
        <AccountingDataTable<CompanyLinkRow>
          columns={linkColumns}
          rows={visibleLinks}
          getRowId={(row) => String(row.id)}
          loading={linksLoading}
          emptyMessage={linksEmptyMessage}
          ariaLabel="firma bağlantıları tablosu"
          columnResizeStorageKey="b2b-company-links"
          defaultRowsPerPage={25}
          rowsPerPageOptions={[10, 25, 50]}
          elevation={0}
        />
      </Paper>

      {/* Elle eşleştirme — otomatik yolların çalışmadığı nadir durumlar için güvenlik ağı */}
      {!isManager && (
        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Elle Eşleştir
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Bir cariniz otomatik eşleşmediyse (vergi no / e-posta tutmuyor ve müşteri Aquasoft
              girişi yapmadıysa) burada elle bağlayabilirsiniz.
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
              options={accountOptions}
              value={manualAccount}
              loading={accountSearchLoading}
              onChange={(_, v) => setManualAccount(v)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') loadAccountOptions(value || undefined);
              }}
              getOptionLabel={(o) => `${o.name}${o.accountCode ? ` (${o.accountCode})` : ''}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Bağlantısız cari bulunamadı"
              renderInput={(params) => (
                <TextField {...params} label="Cari seçin" placeholder="Cari adı, vergi no veya e-posta" />
              )}
            />
            <Autocomplete
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
              options={companyOptions}
              value={manualCompany}
              loading={companySearchLoading}
              onChange={(_, v) => setManualCompany(v)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') loadCompanyOptions(value);
              }}
              getOptionLabel={(o) => o.taxNumberMasked ? `${o.name} (VKN: ${o.taxNumberMasked})` : o.name}
              isOptionEqualToValue={(o, v) => o.companyId === v.companyId}
              filterOptions={(x) => x}
              noOptionsText="En az 3 karakter yazın"
              renderInput={(params) => (
                <TextField {...params} label="Aquasoft firması arayın" placeholder="Firma adı veya vergi no" />
              )}
            />
            <Button
              variant="contained"
              startIcon={manualLinking ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
              disabled={!manualAccount || !manualCompany || manualLinking}
              onClick={handleManualLink}
            >
              Bağla
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );

  const orderColumns = useMemo<DataTableColumn<IncomingPurchaseOrderRow>[]>(() => [
    {
      id: 'rowNumber',
      label: 'Sıra',
      width: '56px',
      align: 'center',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {index + 1}
        </Box>
      ),
    },
    {
      id: 'sellerCompanyName',
      label: 'Satıcı Firma',
      field: 'sellerCompanyName',
    },
    {
      id: 'sellerOrderNumber',
      label: 'Sipariş No',
      field: 'sellerOrderNumber',
    },
    ...(!isManager
      ? [{
          id: 'branch',
          label: 'Şube',
          render: (row: IncomingPurchaseOrderRow) => (
            <Chip
              size="small"
              variant="outlined"
              color={row.branchId ? 'primary' : 'default'}
              label={branchLabel(row.branchId)}
            />
          ),
        } as DataTableColumn<IncomingPurchaseOrderRow>]
      : []),
    {
      id: 'orderedAt',
      label: 'Tarih',
      render: (row) => formatDate(row.orderedAt),
    },
    {
      id: 'totalAmount',
      label: 'Tutar',
      align: 'right',
      render: (row) => formatMoney(row.totalAmount, row.currency),
    },
    {
      id: 'status',
      label: 'Durum',
      render: (row) => statusChip(row.status),
    },
    {
      id: 'itemCount',
      label: 'Kalem',
      render: (row) => (
        <Box component="span">
          {row.itemCount} kalem
          {row.unmatchedCount > 0 && !row.isInvoiced && (
            <Chip size="small" color="warning" variant="outlined" label={`${row.unmatchedCount} eşleşmemiş`} sx={{ ml: 1 }} />
          )}
        </Box>
      ),
    },
    {
      id: 'invoice',
      label: 'Fatura',
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          {row.isInvoiced
            ? <Chip size="small" color="success" icon={<ReceiptIcon />} label="Faturalaştı" />
            : <Chip size="small" variant="outlined" label="Bekliyor" />}
          {row.sellerEInvoiceETTN
            ? <Chip size="small" variant="outlined" color="info" label={`E-fatura: ${row.sellerEInvoiceStatus || 'gönderildi'}`} />
            : <Chip size="small" variant="outlined" label="E-fatura bekleniyor" />}
        </Box>
      ),
    },
    {
      id: 'actions',
      label: 'İşlem',
      align: 'right',
      width: '72px',
      render: (row) => (
        <Tooltip title="Detay / Faturalaştır">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openDetail(row.id);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [isManager, branchLabel]);

  const ordersTable = (
    <Paper variant="outlined">
      <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Durum</InputLabel>
          <Select value={statusFilter} label="Durum" onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}>
            <MenuItem value="">Tümü</MenuItem>
            {Object.entries(STATUS_LABELS).map(([key, meta]) => (
              <MenuItem key={key} value={key}>{meta.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Fatura</InputLabel>
          <Select value={invoicedFilter} label="Fatura" onChange={(e) => { setPage(0); setInvoicedFilter(e.target.value); }}>
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="false">Faturalaşmamış</MenuItem>
            <MenuItem value="true">Faturalaşmış</MenuItem>
          </Select>
        </FormControl>
        {!isManager && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Şube</InputLabel>
            <Select value={orderBranchFilter} label="Şube" onChange={(e) => { setPage(0); setOrderBranchFilter(e.target.value); }}>
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="hq">Merkez</MenuItem>
              {linkBranchOptions.map(([id, name]) => (
                <MenuItem key={id} value={id}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Yenile">
          <IconButton onClick={loadOrders} disabled={ordersLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <AccountingDataTable<IncomingPurchaseOrderRow>
        columns={orderColumns}
        rows={orders}
        getRowId={(row) => row.id}
        loading={ordersLoading}
        serverSidePagination
        totalCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage="Gelen alış siparişi yok. Tedarikçinizin B2B sitesinde hesabınızı bağladıysanız, yeni siparişler burada görünecek."
        onRowClick={(row) => openDetail(row.id)}
        ariaLabel="b2b gelen alış siparişleri tablosu"
        columnResizeStorageKey="b2b-incoming-orders"
        elevation={0}
      />
    </Paper>
  );

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 1.5, md: 3 } }}>
      {!embedded && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              B2B Siparişi
            </Typography>
            <GuideButton guideKey="documents.interCompany" size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aquasoft kullanan tedarikçilerinizden verdiğiniz siparişleri takip edin, teslimatta alış faturasına çevirin.
          </Typography>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
          <Tab icon={<MoveToInboxIcon />} iconPosition="start" label="Gelen Alış Siparişleri" />
          <Tab icon={<LinkIcon />} iconPosition="start" label="Firma Bağlantıları" />
        </Tabs>
        {/* Gömülü kullanımda başlık yok: rehber düğmesi sekme satırının sağında */}
        {embedded && <GuideButton guideKey="documents.interCompany" size="small" />}
      </Box>

      {tab === 0 && ordersTable}
      {tab === 1 && linksTable}

      <IncomingOrderDetailDialog
        orderId={detailOrderId}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onChanged={loadOrders}
        branchLabel={branchLabel}
      />

      <Dialog
        open={!!revokeTarget}
        onClose={closeRevokeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bağlantıyı kes</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {revokeTarget
              ? `"${revokeTarget.counterpartCompanyName}" ile bağlantı kesilecek.`
              : ''}
          </DialogContentText>
          {revokeTarget && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                size="small"
                color={revokeTarget.role === 'buyer' ? 'primary' : 'secondary'}
                variant="outlined"
                label={revokeTarget.role === 'buyer' ? 'Alıcı (siz sipariş veriyorsunuz)' : 'Satıcı (size sipariş geliyor)'}
              />
              <Chip
                size="small"
                variant="outlined"
                color={revokeTarget.buyerBranchId ? 'primary' : 'default'}
                label={branchLabel(revokeTarget.buyerBranchId, revokeTarget.buyerBranchName)}
              />
              {revokeTarget.sellerAccountName && (
                <Chip size="small" variant="outlined" label={revokeTarget.sellerAccountName} />
              )}
            </Box>
          )}
          <DialogContentText>
            Yeni siparişler artık aynalanmaz. Mevcut kayıtlar silinmez.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeRevokeDialog} disabled={revoking}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRevoke}
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : <LinkOffIcon />}
          >
            {revoking ? 'Kesiliyor...' : 'Bağlantıyı kes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!reconnectTarget}
        onClose={closeReconnectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Yeniden bağla</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {reconnectTarget
              ? `"${reconnectTarget.counterpartCompanyName}" ile bağlantı yeniden kurulacak.`
              : ''}
          </DialogContentText>
          {reconnectTarget && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                size="small"
                color={reconnectTarget.role === 'buyer' ? 'primary' : 'secondary'}
                variant="outlined"
                label={reconnectTarget.role === 'buyer' ? 'Alıcı (siz sipariş veriyorsunuz)' : 'Satıcı (size sipariş geliyor)'}
              />
              <Chip
                size="small"
                variant="outlined"
                color={reconnectTarget.buyerBranchId ? 'primary' : 'default'}
                label={branchLabel(reconnectTarget.buyerBranchId, reconnectTarget.buyerBranchName)}
              />
              {reconnectTarget.sellerAccountName && (
                <Chip size="small" variant="outlined" label={reconnectTarget.sellerAccountName} />
              )}
            </Box>
          )}
          <DialogContentText>
            Yeni siparişler tekrar aynalanır. Mevcut kayıtlar değişmez.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeReconnectDialog} disabled={reconnecting}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={confirmReconnect}
            disabled={reconnecting}
            startIcon={reconnecting ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          >
            {reconnecting ? 'Bağlanıyor...' : 'Yeniden bağla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} variant="filled">
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InterCompanyB2B;
